/**
 * Pure helpers for splitting lesson markdown strings into renderable segments.
 * Used by LearningRichContent (proactive + remedial).
 */

/**
 * LLMs often emit mermaid as remark-style containers (`:::mermaid` … `:::`).
 * Our pipeline only understands fenced ```mermaid blocks, so normalize first.
 */
function directiveMermaidToFences(text: string): string {
  return text.replace(
    /:::+\s*mermaid\s*\r?\n([\s\S]*?)\r?\n:::+\s*/gi,
    (_, inner: string) => "```mermaid\n" + inner.trim() + "\n```\n",
  );
}

/** Split body text into alternating markdown and ```mermaid``` blocks. */
export function splitMarkdownAndMermaid(
  content: string
): Array<{ type: "md"; value: string } | { type: "mermaid"; value: string }> {
  const normalized = directiveMermaidToFences(content);
  const segments: Array<{ type: "md"; value: string } | { type: "mermaid"; value: string }> = [];
  const re = /```mermaid\s*([\s\S]*?)```/gi;
  let lastEnd = 0;
  let m;
  while ((m = re.exec(normalized)) !== null) {
    if (m.index > lastEnd) {
      const md = normalized.slice(lastEnd, m.index).trim();
      if (md) segments.push({ type: "md", value: md });
    }
    const chart = m[1].trim();
    if (chart) segments.push({ type: "mermaid", value: chart });
    lastEnd = re.lastIndex;
  }
  if (lastEnd < normalized.length) {
    const md = normalized.slice(lastEnd).trim();
    if (md) segments.push({ type: "md", value: md });
  }
  if (segments.length === 0 && normalized.trim()) segments.push({ type: "md", value: normalized.trim() });
  return segments;
}

/** Diagram type line starters — one Mermaid parse often expects one diagram per block. */
const MERMAID_DIAGRAM_STARTERS =
  /^\s*(graph\s|flowchart\s|sequenceDiagram|mindmap\s|stateDiagram|classDiagram|erDiagram|journey|gantt|pie\s|gitGraph|blockDiagram)/m;

/** Split one mermaid string that may contain multiple diagrams into one chart per diagram. */
export function splitMermaidCharts(block: string): string[] {
  const trimmed = block.trim();
  if (!trimmed) return [];
  const parts: string[] = [];
  let lastIndex = 0;
  let match;
  const re = new RegExp(MERMAID_DIAGRAM_STARTERS.source, "gim");
  while ((match = re.exec(trimmed)) !== null) {
    if (match.index > lastIndex) {
      const chunk = trimmed.slice(lastIndex, match.index).trim();
      if (chunk) parts.push(chunk);
    }
    lastIndex = match.index;
  }
  if (lastIndex < trimmed.length) {
    const chunk = trimmed.slice(lastIndex).trim();
    if (chunk) parts.push(chunk);
  }
  return parts.length > 0 ? parts : [trimmed];
}

/** Quote node labels that contain special chars so Mermaid parses them. */
export function normalizeMermaidChart(chart: string): string {
  return chart.replace(/\[([^\]]+)\]/g, (_, inner) => {
    const unquoted = inner.replace(/^["']|["']$/g, "").trim();
    const needsQuotes = /[&/(),\[\]\\]/.test(unquoted) || unquoted.includes('"');
    if (needsQuotes) {
      return `["${unquoted.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"]`;
    }
    return `[${inner}]`;
  });
}

export function isLikelyMermaid(chart: string): boolean {
  const v = chart.trim();
  return /^(graph|flowchart|sequenceDiagram|mindmap|stateDiagram|classDiagram|erDiagram|journey|gantt|pie|gitGraph|blockDiagram)/m.test(
    v
  );
}

/** If `diagram` is bare mermaid (not :::mermaid or fenced), wrap for the markdown pipeline. */
export function ensureMermaidInMarkdownFragment(fragment: string): string {
  const t = fragment.trim();
  if (!t) return "";
  if (/:::+\s*mermaid/i.test(t) || /```\s*mermaid/i.test(t)) return fragment;
  if (isLikelyMermaid(t)) {
    return "```mermaid\n" + t + "\n```";
  }
  return fragment;
}
